import _extends from '@babel/runtime/helpers/esm/extends';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import { ChevronRightIcon, ChevronLeftIcon } from '@paypalcorp/pp-react-icons';
import '../__generated__/styles/pagination.css';

var styles = {"ppvx_pagination":"ppvx_pagination___1-3-8","ppvx_pagination_page-number":"ppvx_pagination_page-number___1-3-8","ppvx_pagination_page-arrow":"ppvx_pagination_page-arrow___1-3-8","ppvx_pagination_current-page":"ppvx_pagination_current-page___1-3-8","ppvx_pagination_ellipses":"ppvx_pagination_ellipses___1-3-8","ppvx_pagination_space":"ppvx_pagination_space___1-3-8","ppvx_pagination_arrow":"ppvx_pagination_arrow___1-3-8","ppvx_pagination_arrow-left":"ppvx_pagination_arrow-left___1-3-8","ppvx_pagination_arrow-right":"ppvx_pagination_arrow-right___1-3-8","ppvx_pagination_arrow-svg-icon":"ppvx_pagination_arrow-svg-icon___1-3-8"};

var _excluded = ["totalPageCount", "currentPage", "prevPageLabel", "nextPageLabel", "paginationLabel", "className", "arrowLeftClassName", "pageClassName", "activePageClassName", "arrowRightClassName", "arrowSpaceClassName", "onChange"];

var Pagination = function (_ref) {
  var totalPageCount = _ref.totalPageCount,
      currentPage = _ref.currentPage,
      _ref$prevPageLabel = _ref.prevPageLabel,
      prevPageLabel = _ref$prevPageLabel === void 0 ? 'previous page' : _ref$prevPageLabel,
      _ref$nextPageLabel = _ref.nextPageLabel,
      nextPageLabel = _ref$nextPageLabel === void 0 ? 'next page' : _ref$nextPageLabel,
      _ref$paginationLabel = _ref.paginationLabel,
      paginationLabel = _ref$paginationLabel === void 0 ? 'Pagination Navigation' : _ref$paginationLabel,
      className = _ref.className,
      arrowLeftClassName = _ref.arrowLeftClassName,
      pageClassName = _ref.pageClassName,
      activePageClassName = _ref.activePageClassName,
      arrowRightClassName = _ref.arrowRightClassName,
      arrowSpaceClassName = _ref.arrowSpaceClassName,
      onChange = _ref.onChange,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      rtl = _useState2[0],
      setRtl = _useState2[1];

  var containerRef = useCallback(function (node) {
    if (node !== null) {
      var computedStyle = window.getComputedStyle(node);
      setRtl(computedStyle && computedStyle.direction === 'rtl');
    }
  }, []);
  /* istanbul ignore next */

  var LeftArrowIcon = rtl ? ChevronRightIcon : ChevronLeftIcon;
  /* istanbul ignore next */

  var RightArrowIcon = rtl ? ChevronLeftIcon : ChevronRightIcon;
  var ellipse = '...';
  var pageClass = clsx(styles['ppvx_pagination_page-number'], pageClassName);
  var paginationEllipses = styles.ppvx_pagination_ellipses;
  var paginationSpace = clsx(styles.ppvx_pagination_space, arrowSpaceClassName);
  var paginationClasses = clsx(styles.ppvx_pagination, className);
  var arrowClassesLeft = clsx(pageClass, styles.ppvx_pagination_arrow, arrowLeftClassName);
  var arrowClassesRight = clsx(pageClass, styles.ppvx_pagination_arrow, arrowRightClassName);
  var activePage = clsx(pageClass, styles['ppvx_pagination_current-page'], activePageClassName);
  var listData = [];
  var paginationArray = [];

  if (totalPageCount < 8) {
    paginationArray = _toConsumableArray(Array(totalPageCount + 1).keys()).slice(1);
  } else if (currentPage < 5) {
    paginationArray = [1, 2, 3, 4, 5, ellipse, totalPageCount];
  } else if (currentPage + 3 >= totalPageCount) {
    paginationArray = [1, ellipse, totalPageCount - 4, totalPageCount - 3, totalPageCount - 2, totalPageCount - 1, totalPageCount];
  } else {
    paginationArray = [1, ellipse, currentPage - 1, currentPage, currentPage + 1, ellipse, totalPageCount];
  }

  var handleClick = function (e, pageNumber) {
    e.preventDefault();
    onChange(pageNumber);
  };

  if (totalPageCount > 1) {
    listData = []; // left arrow

    listData.push(currentPage !== 1 ? /*#__PURE__*/React.createElement("li", {
      key: "left-arrow"
    }, /*#__PURE__*/React.createElement("a", {
      href: "#",
      className: arrowClassesLeft,
      "aria-label": prevPageLabel,
      onClick: function onClick(e) {
        return handleClick(e, currentPage - 1);
      }
    }, /*#__PURE__*/React.createElement(LeftArrowIcon, {
      className: styles['ppvx_pagination_arrow-svg-icon']
    }))) : /*#__PURE__*/React.createElement("li", {
      key: "left-arrow",
      className: paginationSpace,
      "aria-disabled": "true"
    })); // actual page numbers

    var finalClass = '';
    var ariaCurrent = null;

    var _loop = function (i) {
      if (paginationArray[i] === ellipse) {
        listData.push( /*#__PURE__*/React.createElement("li", {
          role: "none",
          key: "k-".concat(paginationArray[i], "-").concat(i),
          className: paginationEllipses,
          "aria-disabled": "true"
        }, "".concat(paginationArray[i])));
      } else {
        if (paginationArray[i] === currentPage) {
          finalClass = activePage;
          ariaCurrent = 'page';
        } else {
          finalClass = pageClass;
          ariaCurrent = null;
        }

        listData.push( /*#__PURE__*/React.createElement("li", {
          key: "k-".concat(paginationArray[i])
        }, /*#__PURE__*/React.createElement("a", {
          href: "#",
          className: finalClass // @ts-ignore
          ,
          "aria-current": ariaCurrent // As per the a11y guidelines we should not be updating this as part of world ready
          // eslint-disable-next-line no-loop-func
          ,
          onClick: function onClick(e) {
            return handleClick(e, paginationArray[i]);
          }
        }, "".concat(paginationArray[i]))));
      }
    };

    for (var i = 0; i < paginationArray.length; i++) {
      _loop(i);
    } // right arrow


    listData.push(currentPage < totalPageCount ? /*#__PURE__*/React.createElement("li", {
      key: "right-arrow"
    }, /*#__PURE__*/React.createElement("a", {
      href: "#",
      className: arrowClassesRight,
      "aria-label": nextPageLabel,
      onClick: function onClick(e) {
        return handleClick(e, currentPage + 1);
      }
    }, /*#__PURE__*/React.createElement(RightArrowIcon, {
      className: styles['ppvx_pagination_arrow-svg-icon']
    }))) : /*#__PURE__*/React.createElement("li", {
      key: "right-arrow",
      className: paginationSpace,
      "aria-disabled": "true"
    }));
  }

  return /*#__PURE__*/React.createElement("nav", _extends({
    ref: containerRef
  }, rest, {
    "aria-label": paginationLabel
  }), /*#__PURE__*/React.createElement("ul", {
    className: paginationClasses
  }, listData));
};

export { Pagination };
